import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import { supabase } from '../../../lib/supabase';

const UserDetailsPanel = ({ user, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedUser, setEditedUser] = useState(user);
  const [permissions, setPermissions] = useState(null);
  const [activityLogs, setActivityLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);

  useEffect(() => {
    setEditedUser(user);
    fetchPermissions(user?.role);
    fetchActivityLogs(user?.id);
  }, [user]);

  const fetchPermissions = async (role) => {
    try {
      const { data, error } = await supabase
        ?.from('role_permissions')
        ?.select('*')
        ?.eq('role', role)
        ?.single();

      if (error) throw error;
      setPermissions(data);
    } catch (error) {
      console.error('Error fetching permissions:', error);
    }
  };

  const fetchActivityLogs = async (userId) => {
    setLoadingLogs(true);
    try {
      const { data, error } = await supabase
        ?.from('activity_logs')
        ?.select('*')
        ?.eq('user_id', userId)
        ?.order('timestamp', { ascending: false })
        ?.limit(10);

      if (error) throw error;
      setActivityLogs(data || []);
    } catch (error) {
      console.error('Error fetching activity logs:', error);
    } finally {
      setLoadingLogs(false);
    }
  };

  const handleSave = async () => {
    await onUpdate(user?.id, {
      full_name: editedUser?.full_name,
      role: editedUser?.role,
      status: editedUser?.status,
      department: editedUser?.department,
      phone: editedUser?.phone
    });
    setIsEditing(false);
    fetchPermissions(editedUser?.role);
  };

  const handleCancel = () => {
    setEditedUser(user);
    setIsEditing(false);
  };

  const roleOptions = [
    { value: 'coordinator', label: 'Coordinator - Entry Only' },
    { value: 'supervisor', label: 'Supervisor - Entry + Team Reports' },
    { value: 'manager', label: 'Manager - Full Reports + User Management' },
    { value: 'administrator', label: 'Administrator - System Configuration' }
  ];

  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'suspended', label: 'Suspended' }
  ];

  const getStatusBadgeColor = (status) => {
    const colors = {
      active: 'bg-success/10 text-success',
      inactive: 'bg-muted text-muted-foreground',
      suspended: 'bg-error/10 text-error'
    };
    return colors?.[status] || 'bg-muted text-muted-foreground';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString)?.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-card rounded-lg shadow-elevation-2 border border-border">
      <div className="p-24 md:p-32 border-b border-border">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-16">
            {user?.avatar_url ? (
              <img
                src={user?.avatar_url}
                alt={user?.full_name || 'User avatar'}
                className="w-[64px] h-[64px] rounded-full object-cover"
              />
            ) : (
              <div className="w-[64px] h-[64px] rounded-full bg-primary/10 flex items-center justify-center">
                <Icon name="User" size={32} color="var(--color-primary)" />
              </div>
            )}
            <div>
              <h2 className="font-heading text-2xl font-bold text-foreground mb-4">
                {user?.full_name || 'Unknown User'}
              </h2>
              <p className="text-sm text-muted-foreground mb-8">{user?.email}</p>
              <span
                className={`text-xs px-12 py-4 rounded-full font-medium ${
                  getStatusBadgeColor(user?.status)
                }`}
              >
                {user?.status?.charAt(0)?.toUpperCase() + user?.status?.slice(1)}
              </span>
            </div>
          </div>
          <div className="flex gap-8">
            {isEditing ? (
              <>
                <Button variant="outline" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button onClick={handleSave}>
                  Save Changes
                </Button>
              </>
            ) : (
              <Button iconName="Edit" onClick={() => setIsEditing(true)}>
                Edit User
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="p-24 md:p-32">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-24 mb-32">
          <div>
            <h3 className="font-heading text-lg font-semibold text-foreground mb-16 flex items-center gap-8">
              <Icon name="User" size={18} color="var(--color-primary)" />
              User Information
            </h3>
            <div className="space-y-16">
              {isEditing ? (
                <>
                  <Input
                    label="Full Name"
                    value={editedUser?.full_name || ''}
                    onChange={(e) =>
                      setEditedUser({ ...editedUser, full_name: e?.target?.value })
                    }
                  />
                  <Input
                    label="Department"
                    value={editedUser?.department || ''}
                    onChange={(e) =>
                      setEditedUser({ ...editedUser, department: e?.target?.value })
                    }
                  />
                  <Input
                    label="Phone"
                    value={editedUser?.phone || ''}
                    onChange={(e) =>
                      setEditedUser({ ...editedUser, phone: e?.target?.value })
                    }
                  />
                </>
              ) : (
                <>
                  <div>
                    <p className="text-xs text-muted-foreground mb-4">Department</p>
                    <p className="text-sm text-foreground">
                      {user?.department || 'Not specified'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-4">Phone</p>
                    <p className="text-sm text-foreground">{user?.phone || 'Not specified'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-4">Last Login</p>
                    <p className="text-sm text-foreground">{formatDate(user?.last_login)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-4">Member Since</p>
                    <p className="text-sm text-foreground">{formatDate(user?.created_at)}</p>
                  </div>
                </>
              )}
            </div>
          </div>

          <div>
            <h3 className="font-heading text-lg font-semibold text-foreground mb-16 flex items-center gap-8">
              <Icon name="Shield" size={18} color="var(--color-primary)" />
              Role & Permissions
            </h3>
            <div className="space-y-16">
              {isEditing ? (
                <>
                  <Select
                    label="Role"
                    options={roleOptions}
                    value={editedUser?.role}
                    onChange={(value) => {
                      setEditedUser({ ...editedUser, role: value });
                      fetchPermissions(value);
                    }}
                  />
                  <Select
                    label="Status"
                    options={statusOptions}
                    value={editedUser?.status}
                    onChange={(value) => setEditedUser({ ...editedUser, status: value })}
                  />
                </>
              ) : (
                <div>
                  <p className="text-xs text-muted-foreground mb-4">Current Role</p>
                  <p className="text-sm text-foreground font-medium mb-16">
                    {user?.role?.charAt(0)?.toUpperCase() + user?.role?.slice(1)}
                  </p>
                </div>
              )}

              {permissions && (
                <div className="bg-muted/30 rounded-lg p-16 space-y-8">
                  <p className="text-xs font-semibold text-foreground mb-12">Permissions</p>
                  <div className="space-y-6">
                    <PermissionItem
                      label="Tracking Entry"
                      granted={permissions?.can_entry}
                    />
                    <PermissionItem
                      label="Search Access"
                      granted={permissions?.can_search}
                    />
                    <PermissionItem
                      label="Team Reports"
                      granted={permissions?.can_view_team_reports}
                    />
                    <PermissionItem
                      label="All Reports"
                      granted={permissions?.can_view_all_reports}
                    />
                    <PermissionItem
                      label="User Management"
                      granted={permissions?.can_manage_users}
                    />
                    <PermissionItem
                      label="System Configuration"
                      granted={permissions?.can_system_config}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div>
          <h3 className="font-heading text-lg font-semibold text-foreground mb-16 flex items-center gap-8">
            <Icon name="Activity" size={18} color="var(--color-primary)" />
            Recent Activity
          </h3>
          {loadingLogs ? (
            <div className="flex items-center justify-center py-24">
              <Icon name="Loader2" size={24} className="animate-spin" color="var(--color-primary)" />
            </div>
          ) : activityLogs?.length === 0 ? (
            <p className="text-sm text-muted-foreground">No recent activity</p>
          ) : (
            <div className="space-y-12">
              {activityLogs?.map((log) => (
                <div
                  key={log?.id}
                  className="flex items-start gap-12 p-12 bg-muted/30 rounded-lg"
                >
                  <Icon name="Circle" size={8} color="var(--color-primary)" className="mt-6" />
                  <div className="flex-1">
                    <p className="text-sm text-foreground">
                      {log?.action?.replace(/_/g, ' ')?.charAt(0)?.toUpperCase() +
                        log?.action?.replace(/_/g, ' ')?.slice(1)}
                    </p>
                    <p className="text-xs text-muted-foreground mt-2">
                      {formatDate(log?.timestamp)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const PermissionItem = ({ label, granted }) => (
  <div className="flex items-center justify-between">
    <span className="text-xs text-foreground">{label}</span>
    {granted ? (
      <Icon name="Check" size={14} color="var(--color-success)" />
    ) : (
      <Icon name="X" size={14} color="var(--color-muted-foreground)" />
    )}
  </div>
);

export default UserDetailsPanel;